import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Article {
  id: string;
  title: string;
  subtitle: string | null;
  content: string;
  image_url: string | null;
  author: string;
  category: string;
  meta_title: string | null;
  meta_description: string | null;
  keywords: string | null;
  canonical_url: string | null;
  slug: string;
  is_published: boolean;
  read_time: number;
  created_at: string;
  updated_at: string;
}

export const useArticles = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchArticles = async (includeUnpublished = false) => {
    try {
      console.log('🔍 Début du chargement des articles...');
      setLoading(true);
      setError(null);
      
      console.log('🔗 Construction de la requête Supabase...');
      let query = supabase
        .from('articles')
        .select('*')
        .order('created_at', { ascending: false });

      if (!includeUnpublished) {
        query = query.eq('is_published', true);
      }

      console.log('📡 Exécution de la requête...');
      const { data, error } = await query;

      if (error) {
        console.error('❌ Erreur Supabase:', error);
        throw error;
      }

      console.log('✅ Données reçues:', data?.length, 'articles');
      setArticles(data || []);
      setError(null);
    } catch (err) {
      console.error('💥 Erreur lors du chargement des articles:', err);
      
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      
      // Message d'erreur plus simple
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
      console.log('🏁 Fin du chargement des articles');
    }
  };

  const createArticle = async (articleData: {
    title: string;
    subtitle?: string;
    content: string;
    image_url?: string;
    author?: string;
    category?: string;
    meta_title?: string;
    meta_description?: string;
    keywords?: string;
    canonical_url?: string;
    slug: string;
    read_time?: number;
  }) => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .insert([articleData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      setArticles(prev => [data, ...prev]);
      toast.success('Article créé avec succès');
      return data;
    } catch (err) {
      console.error('Erreur lors de la création de l\'article:', err);
      toast.error('Erreur lors de la création de l\'article');
      throw err;
    }
  };

  const updateArticle = async (id: string, updates: Partial<Article>) => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      setArticles(prev => prev.map(article => 
        article.id === id ? data : article
      ));
      toast.success('Article mis à jour avec succès');
      return data;
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'article:', err);
      toast.error('Erreur lors de la mise à jour de l\'article');
      throw err;
    }
  };

  const deleteArticle = async (id: string) => {
    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      setArticles(prev => prev.filter(article => article.id !== id));
      toast.success('Article supprimé avec succès');
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'article:', err);
      toast.error('Erreur lors de la suppression de l\'article');
      throw err;
    }
  };

  const togglePublished = async (id: string, isPublished: boolean) => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .update({ is_published: isPublished })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      setArticles(prev => prev.map(article => 
        article.id === id ? data : article
      ));
      toast.success(`Article ${isPublished ? 'publié' : 'dépublié'} avec succès`);
      return data;
    } catch (err) {
      console.error('Erreur lors de la modification du statut:', err);
      toast.error('Erreur lors de la modification du statut');
      throw err;
    }
  };

  useEffect(() => {
    // Ne charge que les articles publiés par défaut
    // L'admin doit appeler fetchArticles(true) manuellement
  }, []);

  return {
    articles,
    loading,
    error,
    fetchArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    togglePublished,
    refetch: fetchArticles
  };
};